/**
 * Branded type for ISO 8601 date-time strings
 * Format: YYYY-MM-DDTHH:mm:ss±HH:mm or YYYY-MM-DDTHH:mm:ssZ
 * @example "2025-11-15T10:00:00-08:00"
 * @example "2025-11-15T10:00:00Z"
 */
export type ISODateTimeString = string & { readonly __brand: 'ISODateTimeString' };

/**
 * Common IANA timezone identifiers
 * This is not exhaustive but covers the most commonly used timezones in North America
 */
export type IANATimezone =
  // US Timezones
  | 'America/New_York' // Eastern Time
  | 'America/Chicago' // Central Time
  | 'America/Denver' // Mountain Time
  | 'America/Phoenix' // Mountain Time (no DST)
  | 'America/Los_Angeles' // Pacific Time
  | 'America/Anchorage' // Alaska Time
  | 'Pacific/Honolulu' // Hawaii Time
  // Canada
  | 'America/Toronto' // Eastern Time
  | 'America/Vancouver' // Pacific Time
  // UTC
  | 'UTC'
  // Europe (common)
  | 'Europe/London'
  | 'Europe/Paris'
  | 'Europe/Berlin'
  // Asia (common)
  | 'Asia/Tokyo'
  | 'Asia/Shanghai'
  | 'Asia/Singapore'
  // Australia (common)
  | 'Australia/Sydney'
  | 'Australia/Melbourne';

/**
 * Formats an ISO date string into a human-readable date string in the user's local timezone.
 *
 * @param dateString - The date string to format. Can be:
 *   - Full ISO string with timezone: '2025-09-06T01:31:08Z' (UTC)
 *   - Full ISO string without timezone: '2025-09-06T01:31:08' (treated as local time)
 *   - Date-only string: '2025-09-06' (treated as UTC midnight - may cause timezone issues)
 *
 * @returns A formatted date string in the format "Month Day, Year" (e.g., "September 5, 2025")
 *          Returns empty string if the input is invalid or empty.
 *
 * @example
 * // Full ISO string with timezone (recommended)
 * formatDateFromISO('2025-09-06T01:31:08Z') // "September 5, 2025" (in Pacific Time)
 *
 * @example
 * // Date-only string (may cause timezone issues)
 * formatDateFromISO('2023-10-01') // "September 30, 2023" (in Pacific Time) or "October 1, 2023" (in UTC)
 *
 * @example
 * // Invalid input
 * formatDateFromISO('invalid-date') // ""
 * formatDateFromISO('') // ""
 *
 * @note
 * - The function uses the user's local timezone for display
 * - Date-only strings (YYYY-MM-DD) are treated as UTC midnight, which may cause
 *   the displayed date to be one day earlier in timezones behind UTC
 * - For consistent behavior across timezones, use full ISO strings with timezone
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString} toLocaleDateString documentation
 */
export const formatDateFromISO = (dateString: string): string => {
  const date = new Date(dateString);

  if (!date.getTime()) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatToISODate = (date: Date = new Date()): string => date.toISOString().split('.')[0];

export const getCurrentDateString = (date: Date = new Date()): string => date.toISOString().split('T')[0];

/**
 * Formats an ISO date string into a human-readable date and time string.
 *
 * @param dateString - The ISO date string to format (e.g., '2025-11-15T10:00:00-08:00')
 * @returns A formatted string in the format "Month Day, Year at H:MM AM/PM" (e.g., "November 15, 2025 at 10:00 AM")
 *          Returns empty string if the input is invalid or empty.
 *
 * @example
 * formatDateTimeFromISO('2025-11-15T10:00:00-08:00') // "November 15, 2025 at 10:00 AM"
 * formatDateTimeFromISO('2025-12-01T14:30:00-08:00') // "December 1, 2025 at 2:30 PM"
 */
export const formatDateTimeFromISO = (dateString: ISODateTimeString): string => {
  const date = new Date(dateString);

  if (!date.getTime()) {
    return '';
  }

  const dateStr = formatDateFromISO(dateString);

  if (!dateStr) {
    return '';
  }

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateStr} at ${timeStr}`;
};

/**
 * Formats start and end ISO date-time strings into a time range string.
 * Omits minutes when they are "00" and includes timezone abbreviation.
 *
 * @param startDateString - The ISO date-time string for the start time
 * @param endDateString - The ISO date-time string for the end time
 * @param timezone - Optional IANA timezone identifier (e.g., 'America/Chicago'). Defaults to browser's local timezone.
 * @returns A formatted time range string (e.g., "10AM-12PM CST", "9:30AM-11AM PST")
 *          Returns empty string if either input is invalid.
 *
 * @example
 * formatTimeRange('2025-11-15T10:00:00-08:00', '2025-11-15T12:00:00-08:00')
 * // "10AM-12PM PST"
 *
 * @example
 * formatTimeRange('2025-11-15T09:30:00-08:00', '2025-11-15T11:00:00-08:00')
 * // "9:30AM-11AM PST"
 *
 * @example
 * formatTimeRange('2025-11-15T10:00:00-08:00', '2025-11-15T12:00:00-08:00', 'America/Chicago')
 * // "12PM-2PM CST"
 */
export const formatTimeRange = (
  startDateString: ISODateTimeString,
  endDateString: ISODateTimeString,
  timezone?: IANATimezone,
): string => {
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);

  if (!startDate.getTime() || !endDate.getTime()) {
    return '';
  }

  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
    ...(timezone && { timeZone: timezone }),
  };

  // Get formatted times with timezone
  const startFormatted = startDate.toLocaleTimeString('en-US', formatOptions);
  const endFormatted = endDate.toLocaleTimeString('en-US', formatOptions);

  // Extract time and timezone parts
  // Format is typically "10:00 AM PST" or "10:00 AM"
  const startParts = startFormatted.split(' ');
  const endParts = endFormatted.split(' ');

  const startTime = startParts[0];
  const startPeriod = startParts[1]; // AM/PM
  const timezoneAbbr = startParts[2] || ''; // PST, CST, etc.

  const endTime = endParts[0];
  const endPeriod = endParts[1]; // AM/PM

  // Remove ":00" from times
  const formatTime = (time: string, period: string): string => {
    const cleanTime = time.replace(':00', '');

    return `${cleanTime}${period}`;
  };

  const formattedStart = formatTime(startTime, startPeriod);
  const formattedEnd = formatTime(endTime, endPeriod);

  // Build the final string
  return `${formattedStart}-${formattedEnd}${timezoneAbbr ? ` ${timezoneAbbr}` : ''}`;
};

/**
 * Calculates the time remaining until a given start date and returns a formatted string.
 * Shows hours if less than 24 hours remain, otherwise shows days.
 * If the date is in the past or invalid, returns an empty string.
 *
 * @param dateString - An ISO 8601 date string (e.g., "2025-11-15T10:00:00Z")
 * @returns A formatted string showing time until start (e.g., "8 hours", "5 days")
 *          Returns empty string if the input is invalid or the date is in the past.
 *
 * @example
 * getTimeUntilStart('2025-11-15T18:00:00Z') // "8 hours" (if 8 hours remain)
 * getTimeUntilStart('2025-11-20T10:00:00Z') // "5 days" (if 5 days remain)
 * getTimeUntilStart('2025-11-14T10:00:00Z') // "" (date in the past)
 */
export const getTimeUntilStart = (dateString: string): string => {
  const startDate = new Date(dateString);
  const now = new Date();

  if (!startDate.getTime() || startDate <= now) {
    return '';
  }

  // Calculate the difference in milliseconds
  const diffMs = startDate.getTime() - now.getTime();

  // Convert to different units
  const msPerHour = 1000 * 60 * 60;
  const msPerDay = msPerHour * 24;

  // If less than 24 hours, show hours
  if (diffMs < msPerDay) {
    const hours = Math.ceil(diffMs / msPerHour);

    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  // Otherwise show days
  const days = Math.ceil(diffMs / msPerDay);

  return `${days} ${days === 1 ? 'day' : 'days'}`;
};
